import React, { useMemo } from 'react';
import { CheckCircleOutline, HelpOutline, HighlightOffOutlined, SyncOutlined, EditOutlined } from '@material-ui/icons';
import IconButton from '@material-ui/core/IconButton';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@material-ui/icons/Close';
import Popover from '@material-ui/core/Popover';
import Button from '@material-ui/core/Button';
import { css } from '@emotion/react';
import { colors } from '@/styles';
import { BodyText } from './blocks';
import { Grid } from '@material-ui/core';
import { startCase } from 'lodash';
import { asBrowserDate } from '@/utilities/convertToISO';
import { formatPayPeriod, editsDueDate } from '@/utilities/payPeriod';
import { calcSupplementalTotal } from '@onward-delivery/core';

export { ELECTROLUX_COLUMNS, MASTERBRAND_COLUMNS } from '@onward-delivery/core';

const dateNumeric = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

const dollarFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

// The account this invoice is billed TO — i.e. which of the org's own accounts is the payee.
// Only meaningful when several accounts are listed together; see quickbooksCsvColumns below.
const ACCOUNT_CSV_COLUMN = {
    header: 'Account',
    getValue: ({ invoice }) => invoice.client?.business_name || '--',
};

export const QUICKBOOKS_CSV_COLUMNS = [
    {
        header: 'Invoice No.',
        getValue: ({ invoice }) => invoice.invoice_number,
    },
    {
        header: 'Shipper',
        getValue: ({ invoice }) => {
            switch (invoice.type) {
                case 'ONWARD_PAYABLE':
                    return 'Onward Delivery';
                case 'PAYABLE':
                    return invoice.organization?.name || invoice.partner_client?.business_name || 'Unknown';
                case 'INTERNAL':
                    return invoice.partner_client?.business_name || 'Unknown';
            }
        },
    },
    {
        header: 'Partner Client',
        getValue: ({ invoice }) => invoice.partner_client?.business_name || '',
    },
    {
        header: 'Invoice Date',
        getValue: ({ invoice }) => {
            if (!invoice.created_at) {
                return undefined;
            }
            const date = new Date(invoice?.created_at);
            return isNaN(date) ? undefined : date;
        },
        format: (value) => `"${dateNumeric.format(value)}"`,
    },
    {
        header: 'Due Date',
        getValue: ({ invoice }) => {
            if (!invoice.due_date) {
                return undefined;
            }
            const date = new Date(invoice?.due_date);
            return isNaN(date) ? undefined : date;
        },
        format: (value) => `"${dateNumeric.format(value)}"`,
    },
    {
        header: 'Tracking No.',
        getValue: ({ order }) => order?.order_number || '',
    },
    {
        header: 'Purchase Order',
        getValue: ({ order }) => [order.po_number, order.reference_id].filter(Boolean).join(' / '),
    },
    {
        header: 'Product/Service',
        getValue: ({ row }) => {
            return row?.qbo_tag || '';
        },
    },
    {
        header: 'Description',
        getValue: ({ row }) => {
            return row?.description || '';
        },
    },
    {
        header: 'Qty',
        getValue: ({ row }) => {
            return row?.quantity || '';
        },
    },
    {
        header: 'Rate',
        getValue: ({ row }) => {
            return dollarFormatter.format(row?.rate || 0);
        },
    },
    {
        header: 'Amount',
        getValue: ({ row }) => {
            return dollarFormatter.format(row?.total || 0);
        },
    },
    {
        header: 'Class',
        getValue: ({ order }) => {
            return order?.invoice_class || '';
        },
    },
    {
        header: 'Memo',
        getValue: ({ order }) => {
            return [order?.order_number, order?.po_number, order?.reference_id].filter(Boolean).join(' / ');
        },
    },
];

/**
 * CSV columns for the QuickBooks export.
 *
 * At org level a carrier-only org's admin exports several accounts at once, and those accounts
 * are separate legal entities (distinct DBAs) that map to distinct QuickBooks vendors. Without a
 * payee column the rows are unattributable and the import lands against the wrong entity, so the
 * Account column is prepended in that case only.
 *
 * Per-account exports keep the exact column set and order they have always had — those feed
 * existing QuickBooks import templates, which are position-sensitive.
 */
export const quickbooksCsvColumns = (includeAccount) =>
    includeAccount ? [ACCOUNT_CSV_COLUMN, ...QUICKBOOKS_CSV_COLUMNS] : QUICKBOOKS_CSV_COLUMNS;

export const useColumns = ({ callbacks, includeAccount = false }) => {
    return useMemo(() => {
        return [
            {
                Header: 'Data',
                id: 'data-columns',
                columns: [
                    // Org-level only: which of the org's own accounts this invoice is billed to.
                    // Every other column describes the counterparty, so without this a list
                    // spanning several accounts cannot be attributed to any of them.
                    ...(includeAccount
                        ? [
                              {
                                  Header: 'Account',
                                  id: 'account',
                                  span: false,
                                  accessor: (invoice) => invoice.client?.business_name || '--',
                                  Cell: ({ value }) => (
                                      <div
                                          css={css`
                                              overflow: hidden;
                                              height: 100%;
                                          `}
                                      >
                                          <BodyText
                                              css={css`
                                                  white-space: normal;
                                              `}
                                          >
                                              {value}
                                          </BodyText>
                                      </div>
                                  ),
                              },
                          ]
                        : []),
                    {
                        Header: 'Invoice',
                        id: 'invoice_number',
                        accessor: (invoice) => invoice.invoice_number || '--',
                        width: 75,
                    },
                    {
                        Header: 'Shipper',
                        id: 'business_name',
                        span: false,
                        accessor: (invoice) => {
                            switch (invoice.type) {
                                case 'ONWARD_PAYABLE':
                                    return 'Onward Delivery';
                                case 'PAYABLE':
                                    return (
                                        invoice.organization?.name || invoice.partner_client?.business_name || 'Unknown'
                                    );
                                case 'INTERNAL':
                                    return invoice.partner_client?.business_name || 'Unknown';
                            }
                        },
                        Cell: ({ value }) => {
                            return (
                                <div
                                    css={css`
                                        overflow: hidden;
                                        height: 100%;
                                    `}
                                >
                                    <BodyText
                                        css={css`
                                            white-space: normal;
                                        `}
                                    >
                                        {value}
                                    </BodyText>
                                </div>
                            );
                        },
                    },
                    {
                        Header: 'Partner Client',
                        id: 'partner_client',
                        span: false,
                        accessor: (invoice) => invoice.partner_client?.business_name || '--',
                        Cell: ({ value }) => {
                            return (
                                <div
                                    css={css`
                                        overflow: hidden;
                                        height: 100%;
                                    `}
                                >
                                    <BodyText
                                        css={css`
                                            white-space: normal;
                                        `}
                                    >
                                        {value}
                                    </BodyText>
                                </div>
                            );
                        },
                    },
                    {
                        Header: 'Week',
                        id: 'week_number',
                        accessor: (invoice) => invoice.week_number,
                        width: 75,
                    },
                    {
                        Header: 'Pay Period',
                        id: 'period',
                        span: false,
                        accessor: (invoice) => {
                            return formatPayPeriod(invoice.pay_period_start, invoice.pay_period_end) || '--';
                        },
                        Cell: ({ value }) => {
                            return (
                                <div
                                    css={css`
                                        overflow: hidden;
                                        height: 100%;
                                    `}
                                >
                                    <BodyText
                                        css={css`
                                            white-space: normal;
                                        `}
                                    >
                                        {value}
                                    </BodyText>
                                </div>
                            );
                        },
                    },
                    {
                        Header: 'Edits Due',
                        id: 'edits_due',
                        span: false,
                        accessor: (invoice) => {
                            const thurs = editsDueDate(invoice.pay_period_end);
                            if (!thurs) return '--';

                            if (!invoice.is_pending) {
                                return `${dateNumeric.format(new Date(thurs))} (submitted)`;
                            }

                            return dateNumeric.format(thurs);
                        },
                        Cell: ({ value }) => {
                            return (
                                <div
                                    css={css`
                                        overflow: hidden;
                                        height: 100%;
                                    `}
                                >
                                    <BodyText
                                        css={css`
                                            white-space: normal;
                                        `}
                                    >
                                        {value}
                                    </BodyText>
                                </div>
                            );
                        },
                    },
                    {
                        Header: 'Due',
                        id: 'due_date',
                        width: 100,
                        disableSortBy: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            return invoice.due_date ? dateNumeric.format(asBrowserDate(invoice.due_date)) : '--';
                        },
                    },
                    {
                        Header: 'POD',
                        id: 'pod',
                        width: 75,
                        Cell: ({ row }) => {
                            const invoice = row.original;

                            const isMissing = [
                                'orders',
                                'middle_mile_carrier_orders',
                                'warehouse_carrier_orders',
                                'contractor_orders',
                            ].some((ordersAttr) => {
                                return (invoice[ordersAttr] || []).some((order) => {
                                    const hasPhoto = order.delivery_photo?.length >= 1 || order.pod?.length >= 1;
                                    const hasSignature = order.customer_signature?.length >= 1;
                                    return !hasSignature && !hasPhoto;
                                });
                            });

                            return !isMissing ? (
                                <>
                                    <DoneIcon
                                        css={css`
                                            color: ${colors.greens.primary};
                                        `}
                                    />
                                </>
                            ) : (
                                <CloseIcon
                                    css={css`
                                        color: ${colors.reds.primary};
                                        background: none;
                                    `}
                                />
                            );
                        },
                    },
                    {
                        Header: 'Status',
                        id: 'status',
                        width: 125,
                        span: false,
                        disableSortBy: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            const label = startCase(invoice.status.toLowerCase());
                            let Icon = HelpOutline;
                            let color = 'black';
                            switch (invoice.status) {
                                case 'PAID':
                                    color = colors.greens.primary;
                                    Icon = CheckCircleOutline;
                                    break;
                                case 'APPROVED':
                                    color = colors.greens.primary;
                                    Icon = CheckCircleOutline;
                                    break;
                                case 'UNPAID':
                                case 'CANCELLED':
                                    color = colors.reds.cancelled;
                                    Icon = HighlightOffOutlined;
                                    break;
                                case 'PROCESSING':
                                    color = colors.oranges.primary;
                                    Icon = SyncOutlined;
                                    break;
                                case 'DRAFT':
                                    color = colors.greys.secondary;
                                    Icon = EditOutlined;
                                    break;
                                default:
                                    color = 'black';
                                    Icon = HelpOutline;
                                    break;
                            }

                            return (
                                <Grid
                                    container
                                    direction="row"
                                    css={css`
                                        color: ${color};
                                        height: 100%;
                                        align-items: center;
                                        flex-wrap: nowrap;
                                    `}
                                >
                                    <Grid
                                        container
                                        direction="column"
                                        css={css`
                                            flex: 0;
                                            flex-basis: 0;
                                            margin-right: 4px;
                                        `}
                                    >
                                        <Icon />
                                    </Grid>
                                    <Grid
                                        container
                                        direction="column"
                                        css={css`
                                            flex: 0;
                                            flex-basis: 0;
                                        `}
                                    >
                                        <BodyText>{label}</BodyText>
                                    </Grid>
                                </Grid>
                            );
                        },
                    },
                    {
                        Header: 'Subtotal',
                        id: 'subtotal',
                        width: 125,
                        canSort: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;

                            const carrierAmount = (invoice.orders || []).reduce((acc, order) => {
                                const total = order.carrier_rate || 0;
                                return acc + total;
                            }, 0);
                            const mmCarrierAmount = (invoice.middle_mile_carrier_orders || []).reduce((acc, order) => {
                                const total = order.middle_mile_carrier_rate || 0;
                                return acc + total;
                            }, 0);
                            const whCarrierAmount = (invoice.warehouse_carrier_orders || []).reduce((acc, order) => {
                                const total = order.warehouse_carrier_rate || 0;
                                return acc + total;
                            }, 0);
                            const contractorAmount = (invoice.contractor_orders || []).reduce((acc, order) => {
                                const total = order.contractor_rate || 0;
                                return acc + total;
                            }, 0);
                            const amount = carrierAmount + mmCarrierAmount + whCarrierAmount + contractorAmount;

                            return dollarFormatter.format(amount);
                        },
                    },
                    {
                        Header: 'Claims',
                        id: 'claims',
                        width: 125,
                        canSort: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            const claimsTotal = (invoice.claim_adjustments || []).reduce((acc, { amount }) => {
                                return acc + amount;
                            }, 0);
                            return `(${dollarFormatter.format(claimsTotal)})`;
                        },
                    },
                    {
                        Header: 'Total Due',
                        id: 'revenue',
                        width: 125,
                        canSort: false,
                        Cell: ({ row }) => {
                            const invoice = row.original;

                            const carrierAmount = (invoice.orders || []).reduce((acc, order) => {
                                const total = order.carrier_rate || 0;
                                return acc + total;
                            }, 0);
                            const mmCarrierAmount = (invoice.middle_mile_carrier_orders || []).reduce((acc, order) => {
                                const total = order.middle_mile_carrier_rate || 0;
                                return acc + total;
                            }, 0);
                            const whCarrierAmount = (invoice.warehouse_carrier_orders || []).reduce((acc, order) => {
                                const total = order.warehouse_carrier_rate || 0;
                                return acc + total;
                            }, 0);
                            const contractorAmount = (invoice.contractor_orders || []).reduce((acc, order) => {
                                const total = order.contractor_rate || 0;
                                return acc + total;
                            }, 0);
                            const ordersTotal = carrierAmount + mmCarrierAmount + whCarrierAmount + contractorAmount;

                            const claimsTotal = (invoice.claim_adjustments || []).reduce((acc, { amount }) => {
                                return acc + amount;
                            }, 0);

                            return dollarFormatter.format(ordersTotal - claimsTotal + calcSupplementalTotal(invoice));
                        },
                    },
                ],
            },
            {
                sticky: 'right',
                Header: 'Sticky',
                id: 'sticky-group',
                columns: [
                    {
                        Header: '',
                        id: 'action-1',
                        width: 45,
                        Cell: ({ row }) => {
                            const invoice = row.original;
                            const [anchorEl, setAnchorEl] = React.useState(null);

                            const handleClick = (event) => {
                                event.stopPropagation();
                                event.preventDefault();
                                setAnchorEl(event.currentTarget);
                            };

                            const handleClose = (event) => {
                                event?.stopPropagation();
                                event?.preventDefault();
                                setAnchorEl(null);
                            };

                            return (
                                <div
                                    css={css`
                                        color: #59b863;
                                        font-weight: 600;
                                        display: flex-start;
                                        justify-content: flex-start;
                                        align-items: center;
                                    `}
                                >
                                    <IconButton
                                        disabled={invoice.status === 'PAID'}
                                        onClick={handleClick}
                                        css={css`
                                            font-weight: 600;
                                            padding: 4px;
                                            justify-content: center;
                                        `}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                    <Popover
                                        open={Boolean(anchorEl)}
                                        anchorEl={anchorEl}
                                        onClose={handleClose}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'center',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'center',
                                        }}
                                        style={{ padding: '10px', borderRadius: '20px' }}
                                    >
                                        <Grid direction="column" container>
                                            {invoice.type === 'INTERNAL' && ['DRAFT'].includes(invoice.status) && (
                                                <Grid direction="row" container>
                                                    <Button
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            callbacks.submitId(invoice.carrier_invoice_id);
                                                            handleClose();
                                                        }}
                                                        css={css`
                                                            width: 100%;
                                                            justify-content: flex-start;
                                                        `}
                                                    >
                                                        Submit
                                                    </Button>
                                                </Grid>
                                            )}
                                            {invoice.type === 'INTERNAL' && ['UNPAID'].includes(invoice.status) && (
                                                <Grid direction="row" container>
                                                    <Button
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            callbacks.approveId(invoice.carrier_invoice_id);
                                                            handleClose();
                                                        }}
                                                        css={css`
                                                            width: 100%;
                                                            justify-content: flex-start;
                                                        `}
                                                    >
                                                        Approve
                                                    </Button>
                                                </Grid>
                                            )}
                                            {invoice.type === 'INTERNAL' &&
                                                ['UNPAID', 'APPROVED'].includes(invoice.status) && (
                                                    <Grid direction="row" container>
                                                        <Button
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                callbacks.payId(invoice.carrier_invoice_id);
                                                                handleClose();
                                                            }}
                                                            css={css`
                                                                width: 100%;
                                                                justify-content: flex-start;
                                                            `}
                                                        >
                                                            Mark as Paid
                                                        </Button>
                                                    </Grid>
                                                )}
                                            {invoice.type === 'INTERNAL' &&
                                                !['APPROVED', 'PAID'].includes(invoice.status) && (
                                                    <Grid direction="row" container>
                                                        <Button
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                callbacks.cancelId(invoice.carrier_invoice_id);
                                                                handleClose();
                                                            }}
                                                            css={css`
                                                                width: 100%;
                                                                justify-content: flex-start;
                                                            `}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Grid>
                                                )}
                                        </Grid>
                                    </Popover>
                                </div>
                            );
                        },
                        span: false,
                    },
                ],
            },
        ];
    }, [callbacks, includeAccount]);
};
